import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { LiveIcon, PollingIcon } from '../../components/ModeIcons';
import { useIsOperator } from '../auth/hooks/useIsOperator';
import { AnalyzerMode } from './types';

import { useAnalyzer } from './api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useUpdateAnalyzer } from './api/updateAnalyzer';
import { useIsAnalyzerInCrosspolMode } from './hooks/useIsAnalyzerInCrosspolMode';
import { useIsSnapshotLocked } from './hooks/useIsSnapshotLocked';
import { useIsGeoEnabled } from 'features/analyzerModules/api/getAnalyzerModules';
import { grey } from '@mui/material/colors';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { updateAnalyzerProperties } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';

interface Props {
  selectedAnalyzerId: number;
  activeSnapshotId?: number;
  showAnalyzerState?: boolean;
}

const AnalyzerModeBar = ({
  selectedAnalyzerId,
  activeSnapshotId,
  showAnalyzerState,
}: Props) => {
  const dispatch = useAppDispatch();
  const isOperator = useIsOperator();
  const { user } = useAppSelector((state) => state.auth);
  const { data: snapshot } = useSnapshot({
    snapshotId: activeSnapshotId || -1,
  });
  const isSnapshotLocked = useIsSnapshotLocked(snapshot);
  const { data: analyzer } = useAnalyzer({ analyzerId: selectedAnalyzerId });
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const isGeoEnabled = useIsGeoEnabled(selectedAnalyzerId);

  const analyzerInCrossPolMode =
    useIsAnalyzerInCrosspolMode(selectedAnalyzerId);

  const getAnalyzerState = (analyzerMode: number | undefined) => {
    if (analyzerMode === AnalyzerMode.POLLING) return AnalyzerMode.POLLING;
    if (analyzerMode === AnalyzerMode.LIVE) {
      if (showAnalyzerState) return AnalyzerMode.LIVE;

      return activeSnapshotId === analyzer?.active_snapshot
        ? AnalyzerMode.LIVE
        : undefined;
    }
  };

  const setAnalyzerMode = async (
    event: React.MouseEvent,
    newMode: AnalyzerMode
  ) => {
    if (!analyzer) return;
    const analyzerUpdate = {
      ...analyzer,
      mode: newMode,
    };

    if (
      newMode === 0 &&
      activeSnapshotId !== undefined &&
      activeSnapshotId !== -1
    ) {
      analyzerUpdate['active_snapshot'] = activeSnapshotId;

      dispatch(
        updateAnalyzerProperties({
          user_id: user!.id,
          properties: snapshot!,
          analyzer: {
            ...analyzer,
            active_snapshot: activeSnapshotId,
          },
        })
      );
    }

    await updateAnalyzerMutation.mutateAsync({
      data: { ...analyzerUpdate, enabled: !!analyzerUpdate.enabled },
      analyzerId: analyzerUpdate.id,
    });
  };

  return (
    <Grid container wrap='nowrap' alignItems='center' style={{ width: '100%' }}>
      <Grid item>
        <ToggleButtonGroup
          value={getAnalyzerState(analyzer?.mode)}
          exclusive
          onChange={setAnalyzerMode}
          size='small'
          disabled={analyzer?.in_time_share}
        >
          <ToggleButton
            value={AnalyzerMode.LIVE}
            disabled={
              !analyzer ||
              !isOperator ||
              analyzerInCrossPolMode ||
              isSnapshotLocked
            }
          >
            <LiveIcon enabled={analyzer?.mode === AnalyzerMode.LIVE} /> Live
          </ToggleButton>
          {isGeoEnabled && (
            <ToggleButton
              value={AnalyzerMode.POLLING}
              disabled={
                !analyzer ||
                !isOperator ||
                analyzerInCrossPolMode ||
                isSnapshotLocked
              }
            >
              <PollingIcon enabled={analyzer?.mode === AnalyzerMode.POLLING} />{' '}
              Polling
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
      {(analyzerInCrossPolMode || analyzer?.in_time_share) && (
        <Grid item xs>
          <Typography color={grey[500]} sx={{ pl: '.5rem', width: '100%' }}>
            {analyzer?.in_time_share
              ? 'Analyzer is being used in timeshare mode.'
              : 'Turn off crosspol to use live mode.'}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default AnalyzerModeBar;
